import React from "react";
import { LoginWrapper, Button, Checkbox } from "shared";
import "./RodoPage.scss";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useRodoAccept, useGetRodoAcceptance } from "hooks/users";
import { ROUTE_ENUM } from "enums";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useGetMe, useLogout } from "hooks/auth";

type RodoPageProps = {};

const RodoPage: React.FC<RodoPageProps> = () => {
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get("uuid");
  const navigate = useNavigate();

  const { mutate: logout } = useLogout({});
  const { refetch, data: authorizedUser } = useGetMe();
  const { mutate: acceptRodo } = useRodoAccept({
    onSuccess: async () => {
      await refetch();
      navigate(ROUTE_ENUM.LOGIN);
    },
  });
  useGetRodoAcceptance(uuid || "");

  const onSubmit = () => {
    if (uuid) {
      acceptRodo(uuid);
    }
  };

  const validation = () =>
    Yup.object().shape({
      acceptance: Yup.boolean().oneOf([true], "Pole wymagane"),
    });

  const handleLogout = () => {
    logout();
  };

  return (
    <LoginWrapper className="rodo-page">
      <>
        <p className="login-wrapper__subtitle">
          <div className="rodo-container">
            <h1 className="login-wrapper__header">
              Klauzula informacyjna RODO
            </h1>
            <p>
              Zgodnie z art. 13 ust. 1 i 2 Rozporządzenia Parlamentu
              Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r.
              (RODO) informujemy, że:
            </p>

            <h3>1. Administrator Danych Osobowych</h3>
            <p className="indentation">
              Administratorem Państwa danych osobowych jest Reliance Holding sp.
              z o.o., z siedzibą w Krakowie (31-425) przy al.29 Listopada
              39E/U3, NIP: 6751598679. W sprawach związanych z ochroną danych
              osobowych mogą się Państwo kontaktować pod adresem e-mail:
              <a href="mailto:biuro@reliance.pro">biuro@reliance.pro</a> lub
              telefonicznie: 12 345 07 44
            </p>

            <h3>2. Cele i podstawy przetwarzania danych</h3>
            <p className="indentation">
              Państwa dane osobowe przetwarzane są w celu:
            </p>
            <ul>
              <li>
                realizacji usług i zamówień na podstawie art. 6 ust. 1 lit. b
                RODO (niezbędność do wykonania umowy),
              </li>
              <li>
                realizacji obowiązków prawnych, np. księgowych lub podatkowych,
                na podstawie art. 6 ust. 1 lit. c RODO,
              </li>
              <li>
                marketingu bezpośredniego (jeśli wyrażono zgodę), na podstawie
                art. 6 ust. 1 lit. a RODO,
              </li>
              <li>
                dochodzenia ewentualnych roszczeń na podstawie art. 6 ust. 1
                lit. f RODO (prawnie uzasadniony interes administratora).
              </li>
            </ul>

            <h3>3. Odbiorcy danych</h3>
            <p className="indentation">
              Państwa dane mogą być przekazywane podmiotom współpracującym z
              Administratorem, takim jak:
            </p>
            <ul>
              <li>dostawcy usług IT,</li>
              <li>biura rachunkowe,</li>
              <li>firmy kurierskie i pocztowe,</li>
              <li>organy publiczne w zakresie wymagane przepisami prawa.</li>
            </ul>

            <h3>4. Okres przechowywania danych</h3>
            <p className="indentation">Dane osobowe będą przechowywane:</p>
            <ul>
              <li>
                przez czas niezbędny do realizacji umowy oraz przez okres
                wymagany przepisami prawa,
              </li>
              <li>
                w przypadku działań marketingowych – do momentu wycofania zgody.
              </li>
            </ul>

            <h3>5. Prawa Klientów</h3>
            <p className="indentation">Przysługuje Państwu prawo do:</p>
            <ul>
              <li>
                dostępu do danych, ich sprostowania, usunięcia lub ograniczenia
                przetwarzania,
              </li>
              <li>przenoszenia danych,</li>
              <li>wniesienia sprzeciwu wobec przetwarzania danych,</li>
              <li>
                cofnięcia zgody w dowolnym momencie (w przypadku przetwarzania
                na podstawie zgody),
              </li>
              <li>
                wniesienia skargi do organu nadzorczego (Prezesa Urzędu Ochrony
                Danych Osobowych).
              </li>
            </ul>

            <h3>6. Dobrowolność podania danych</h3>
            <p className="indentation">
              Podanie danych osobowych jest dobrowolne, jednak ich brak może
              uniemożliwić realizację usług lub zamówień.
            </p>

            <h3>7. Profilowanie i zautomatyzowane podejmowanie decyzji</h3>
            <p className="indentation">
              Państwa dane osobowe nie są przetwarzane w sposób zautomatyzowany
              ani profilowane.
            </p>

            <p>
              Dziękujemy za zaufanie i współpracę. Jeśli mają Państwo
              jakiekolwiek pytania dotyczące przetwarzania danych osobowych,
              prosimy o kontakt.
            </p>

            <p>
              Z poważaniem,
              <br />
              Reliance Holding
            </p>
          </div>
        </p>
        {uuid && (
          <div className="rodo-page__actions">
            <Formik
              initialValues={{ acceptance: false }}
              onSubmit={(values: any) => onSubmit()}
              validationSchema={validation()}
              enableReinitialize
            >
              {({ errors, values, handleSubmit }) => (
                <Form>
                  <Field
                    id="acceptance"
                    name="acceptance"
                    label={
                      <p>Zgadzam się na warunki umieszczone w klauzuli RODO</p>
                    }
                    as={Checkbox}
                  />
                  <div
                    className={`buttons ${
                      authorizedUser ? "" : "buttons--only-save"
                    }`}
                  >
                    {authorizedUser && (
                      <Button
                        label={"Wyloguj się"}
                        className=" button--stroke "
                        size="large"
                        onClick={() => handleLogout()}
                      />
                    )}
                    <Button
                      label={"Zapisz zmiany"}
                      type="submit"
                      className="button--black button--stroke"
                      size="large"
                      onClick={() => null}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </>
    </LoginWrapper>
  );
};

export default RodoPage;
